import React, { createContext, useContext, useState } from 'react';

import { useConversation } from './hooks/use_conversation';
import { useDropzone } from 'react-dropzone';

import { TranscriptProvider } from './transcript/context';

import type { Agent, Message, PageContext, Paths } from './types';

// Surfaces compose their own chrome around the conversation, so turn state lives here rather than in
// a shared shell that would need a prop for every difference between them.
//
// The seam is chrome, not transport - useAssistant is hardwired, so this context is for the
// assistant's own surfaces (sidebar, mobile) and nothing else. A surface plumbed differently renders
// the transcript through TranscriptContext instead, which carries no transport; see
// transcript/context. Everything still reading this one - the composer, the header, the inline tool
// cards - is assistant-only.
type ConversationContextValue = ReturnType<typeof useConversation> & {
  agents: Agent[];
  firstName?: string | null;
  isAdmin: boolean;
  isDragActive: boolean;
  showAdminDetails: boolean;
  visible: boolean;
  getRootProps: ReturnType<typeof useDropzone>['getRootProps'];
  toggleAdminDetails: () => void;
};

const ConversationContext = createContext<ConversationContextValue | null>(null);

export function useConversationContext() {
  const context = useContext(ConversationContext);
  if (!context) {
    throw new Error('useConversationContext must be used within a ConversationProvider');
  }

  return context;
}

interface Props {
  agents: Agent[];
  children: React.ReactNode;
  firstName?: string | null;
  isAdmin: boolean;
  pageContext: (messages: Message[]) => PageContext | null;
  paths: Paths;
  visible?: boolean;
}

export function ConversationProvider({
  agents,
  children,
  firstName,
  isAdmin,
  pageContext,
  paths,
  visible = true,
}: Props) {
  const conversation = useConversation({ agents, pageContext, paths, visible });
  const [showAdminDetails, setShowAdminDetails] = useState(false);

  const { getRootProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDropAccepted: conversation.addAttachments,
  });

  const value = {
    ...conversation,
    agents,
    firstName,
    getRootProps,
    isAdmin,
    isDragActive,
    showAdminDetails,
    visible,
    toggleAdminDetails: () => setShowAdminDetails(prev => !prev),
  };

  // The transcript subtree reads its own narrower context, so it stays renderable by surfaces
  // that cannot supply the rest of this one. Supplied here so the assistant's surfaces get both
  // from the one provider.
  return (
    <ConversationContext.Provider value={value}>
      <TranscriptProvider
        value={{
          busy: conversation.busy,
          resolveAction: conversation.resolveAction,
          showAdminDetails,
        }}
      >
        {children}
      </TranscriptProvider>
    </ConversationContext.Provider>
  );
}
